import { useEffect, useState } from 'react';
import { Alert, Button, Modal, Spin } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import type { TripRes } from 'api/swagger';
import { useGetTripsByCargoId } from 'api/trips/use-get-trips-by-cargo-id';
import { CargoTripsList } from './cargo-trips-list';
import { PreviewModalContent } from './preview-modal-content';
import { TruckTripsList } from './truck-trips-list';
import type { ICargoMapPreviewProps } from './types';

export const CargoMapPreview: React.FC<ICargoMapPreviewProps> = ({ cargoId, clientOrderNumber }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<TripRes | null>(null);

  const { data: trips, isLoading } = useGetTripsByCargoId({
    cargoId,
    enabled: isModalVisible,
    filters: { statuses: ['ready', 'progress'] },
  });

  useEffect(() => {
    if (isModalVisible && trips?.data.length === 1) {
      setSelectedTrip(trips?.data[0]);
    }
  }, [trips, isModalVisible]);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const clearSelectedTrip = () => {
    setSelectedTrip(null);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    clearSelectedTrip();
  };

  const modalTitle = selectedTrip ? 'Cargo Map' : 'Trips List';
  const tripsLengthValue = trips?.data.length || 0;
  const tripsLength = Boolean(tripsLengthValue);
  const isShowList = !isLoading && !selectedTrip && tripsLength;
  const isNoTrips = !isLoading && !tripsLength;

  return (
    <>
      <Button icon={<GlobalOutlined />} size='small' onClick={showModal} />
      <Modal
        title={modalTitle}
        open={isModalVisible}
        footer={tripsLengthValue > 1 && selectedTrip ? <Button onClick={clearSelectedTrip}>Back to List</Button> : null}
        width='50%'
        onCancel={handleCloseModal}>
        {isLoading && <Spin />}
        {isNoTrips && <Alert message='No trips found' type='info' showIcon />}
        {isShowList && <CargoTripsList trips={trips?.data || []} onTripSelect={setSelectedTrip} />}
        {selectedTrip && (
          <>
            <TruckTripsList
              truckId={selectedTrip.truck?.id}
              currentTrip={selectedTrip}
              onSelectTrip={setSelectedTrip}
            />
            <PreviewModalContent selectedTrip={selectedTrip} clientOrderNumber={clientOrderNumber} />
          </>
        )}
      </Modal>
    </>
  );
};
